import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { billingAPI } from "../../lib/api";
import { formatAUD } from "../../lib/billing";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function CatalogManager({ open, onOpenChange, services, licenseTypes, onRefresh }) {
  const [newService, setNewService] = useState("");
  const [newLicense, setNewLicense] = useState({ name: "", default_price: "" });
  const [busy, setBusy] = useState(false);

  const addService = async () => {
    if (!newService.trim()) return;
    setBusy(true);
    try {
      await billingAPI.addService(newService.trim());
      toast.success(`Service "${newService.trim()}" added`);
      setNewService("");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add service");
    } finally {
      setBusy(false);
    }
  };

  const deleteService = async (item) => {
    try {
      await billingAPI.deleteService(item.id);
      toast.success(`Service "${item.name}" deleted`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete service");
    }
  };

  const addLicenseType = async () => {
    if (!newLicense.name.trim()) return;
    setBusy(true);
    try {
      await billingAPI.addLicenseType({
        name: newLicense.name.trim(),
        default_price: parseFloat(newLicense.default_price) || 0,
      });
      toast.success(`License type "${newLicense.name.trim()}" added`);
      setNewLicense({ name: "", default_price: "" });
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add license type");
    } finally {
      setBusy(false);
    }
  };

  const deleteLicenseType = async (item) => {
    try {
      await billingAPI.deleteLicenseType(item.id);
      toast.success(`License type "${item.name}" deleted`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete license type");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="catalog-manager-dialog">
        <DialogHeader>
          <DialogTitle>Manage Services & License Types</DialogTitle>
          <DialogDescription>
            Services and Office 365 license types you add here are saved and reusable across all customers.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="services">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="services" data-testid="catalog-tab-services">Services</TabsTrigger>
            <TabsTrigger value="licenses" data-testid="catalog-tab-licenses">O365 License Types</TabsTrigger>
          </TabsList>

          <TabsContent value="services" className="space-y-4 pt-2">
            <div className="flex gap-2">
              <Input
                placeholder="New service name (e.g. Managed IT Support)"
                value={newService}
                onChange={(e) => setNewService(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addService()}
                data-testid="catalog-new-service-input"
              />
              <Button onClick={addService} disabled={busy || !newService.trim()} data-testid="catalog-add-service-button">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2" data-testid="catalog-services-list">
              {services.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No services yet. Add your first one above.</p>
              )}
              {services.map((s) => (
                <div key={s.id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="text-sm font-medium text-slate-800">{s.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                    onClick={() => deleteService(s)}
                    data-testid={`catalog-delete-service-${s.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="licenses" className="space-y-4 pt-2">
            <div className="flex gap-2">
              <Input
                placeholder="License type (e.g. Business Premium)"
                value={newLicense.name}
                onChange={(e) => setNewLicense({ ...newLicense, name: e.target.value })}
                data-testid="catalog-new-license-input"
              />
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="$/seat"
                className="w-24"
                value={newLicense.default_price}
                onChange={(e) => setNewLicense({ ...newLicense, default_price: e.target.value })}
                data-testid="catalog-new-license-price-input"
              />
              <Button onClick={addLicenseType} disabled={busy || !newLicense.name.trim()} data-testid="catalog-add-license-button">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2" data-testid="catalog-licenses-list">
              {licenseTypes.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No license types yet. Add e.g. "Business Basic", "Business Premium".</p>
              )}
              {licenseTypes.map((l) => (
                <div key={l.id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-800">{l.name}</span>
                    <Badge variant="secondary" className="text-xs">{formatAUD(l.default_price)}/seat</Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                    onClick={() => deleteLicenseType(l)}
                    data-testid={`catalog-delete-license-${l.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
